import { useEffect, useState } from "react";
import { api, fmtDate, formatApiError } from "@/lib/api";
import { Plus, X, Edit2, Trash2, ArrowLeftRight } from "lucide-react";
import { Pill } from "@/components/badges";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const ROLES = [
  { value: "admin", label: "Administrator", desc: "Full access including user management" },
  { value: "full_access", label: "Full Access", desc: "View & edit all leads/opportunities" },
  { value: "restricted", label: "Restricted", desc: "Own records only" },
];

export default function TeamMembers() {
  const [users, setUsers] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [reassignFrom, setReassignFrom] = useState(null);

  const load = async () => {
    const { data } = await api.get("/users");
    setUsers(data);
  };
  useEffect(() => { load(); }, []);

  const toggleActive = async (u) => {
    if (u.active) {
      await api.delete(`/users/${u.id}`);
      toast.success("User deactivated");
    } else {
      await api.patch(`/users/${u.id}`, { active: true });
      toast.success("User reactivated");
    }
    load();
  };

  const doPermanentDelete = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/users/${confirmDelete.id}?hard=true`);
      toast.success(`${confirmDelete.name} permanently deleted`);
      setConfirmDelete(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="p-8" data-testid="team-page">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="eyb-label">Administration</div>
          <h1 className="font-heading text-3xl font-bold text-slate-900 mt-1">Team Members</h1>
          <p className="text-sm text-slate-500 mt-1">Manage users, access levels and permissions.</p>
        </div>
        <button data-testid="new-user-btn" onClick={() => setShowNew(true)} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#29ABE2] hover:bg-[#1f8ebf] text-white text-sm font-semibold">
          <Plus className="w-4 h-4" /> Add Team Member
        </button>
      </div>

      <div className="eyb-card overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 bg-slate-50/60 border-b border-slate-200">
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">Email</th>
              <th className="px-4 py-3">Role</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Created</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50/50">
                <td className="px-4 py-3 font-semibold text-slate-900">{u.name}</td>
                <td className="px-4 py-3 text-slate-700">{u.email}</td>
                <td className="px-4 py-3"><Pill className="bg-[#29ABE2]/10 text-[#187199] border border-[#29ABE2]/30 capitalize">{u.role.replace("_", " ")}</Pill></td>
                <td className="px-4 py-3">{u.active ? <Pill className="bg-emerald-50 text-emerald-700 border border-emerald-200">Active</Pill> : <Pill className="bg-slate-100 text-slate-600 border border-slate-200">Inactive</Pill>}</td>
                <td className="px-4 py-3 text-xs text-slate-500">{fmtDate(u.created_at)}</td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => setEditing(u)} className="p-2 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded-md mr-1" title="Edit"><Edit2 className="w-3.5 h-3.5" /></button>
                  <button
                    data-testid={`reassign-records-${u.id}`}
                    onClick={() => setReassignFrom(u)}
                    className="inline-flex items-center gap-1 text-xs text-[#1f8ebf] hover:text-[#187199] hover:underline mr-3"
                    title="Reassign all records from this user"
                  >
                    <ArrowLeftRight className="w-3 h-3" /> Reassign
                  </button>
                  <button data-testid={`toggle-active-${u.id}`} onClick={() => toggleActive(u)} className="text-xs text-slate-600 hover:underline mr-3">{u.active ? "Deactivate" : "Reactivate"}</button>
                  <button
                    data-testid={`delete-user-${u.id}`}
                    onClick={() => setConfirmDelete(u)}
                    className="inline-flex items-center gap-1 text-xs text-rose-600 hover:text-rose-700 hover:underline"
                    title="Permanently delete"
                  >
                    <Trash2 className="w-3 h-3" /> Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showNew && <UserModal onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); load(); }} />}
      {editing && <UserModal user={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {reassignFrom && (
        <ReassignAllModal
          fromUser={reassignFrom}
          users={users.filter((u) => u.id !== reassignFrom.id && u.active)}
          onClose={() => setReassignFrom(null)}
          onDone={() => { setReassignFrom(null); load(); }}
        />
      )}

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent data-testid="delete-user-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Permanently delete {confirmDelete?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the user account entirely (not just deactivates it). If this user still owns or created any leads, opportunities or tasks, the delete will be blocked — reassign those records first.<br /><br />
              For most cases, "Deactivate" is the safer choice — it keeps their history intact while preventing login.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-user-delete">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-user-delete" onClick={doPermanentDelete} className="bg-rose-600 hover:bg-rose-700 text-white">Permanently delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function UserModal({ user, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: user?.name || "", email: user?.email || "", password: "",
    role: user?.role || "restricted",
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      if (user) {
        const patch = { name: form.name, role: form.role };
        if (form.password) patch.password = form.password;
        await api.patch(`/users/${user.id}`, patch);
        toast.success("User updated");
      } else {
        await api.post("/users", form);
        toast.success("User created");
      }
      onSaved();
    } catch (err) { toast.error(formatApiError(err)); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="user-modal">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-heading text-lg font-semibold">{user ? "Edit team member" : "New team member"}</h3>
          <button onClick={onClose}><X className="w-4 h-4 text-slate-400" /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <label className="block">
            <span className="eyb-label">Full Name</span>
            <input data-testid="user-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#29ABE2]/40" />
          </label>
          {!user && (
            <label className="block">
              <span className="eyb-label">Email</span>
              <input data-testid="user-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#29ABE2]/40" />
            </label>
          )}
          <label className="block">
            <span className="eyb-label">{user ? "New Password (leave blank to keep)" : "Password"}</span>
            <input data-testid="user-password" type="password" required={!user} minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#29ABE2]/40" />
          </label>
          <div>
            <span className="eyb-label">Access Level</span>
            <div className="mt-2 space-y-2">
              {ROLES.map((r) => (
                <label key={r.value} className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${form.role === r.value ? "border-[#29ABE2] bg-[#29ABE2]/5" : "border-slate-200 hover:border-slate-300"}`}>
                  <input data-testid={`role-${r.value}`} type="radio" name="role" checked={form.role === r.value} onChange={() => setForm({ ...form, role: r.value })} className="mt-1" />
                  <div>
                    <div className="text-sm font-semibold text-slate-800">{r.label}</div>
                    <div className="text-xs text-slate-500">{r.desc}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-50 rounded-md">Cancel</button>
            <button data-testid="submit-user" type="submit" disabled={saving} className="px-4 py-2 bg-[#29ABE2] hover:bg-[#1f8ebf] text-white text-sm font-semibold rounded-full">{saving ? "Saving…" : "Save"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ReassignAllModal({ fromUser, users, onClose, onDone }) {
  const [counts, setCounts] = useState(null);
  const [toUserId, setToUserId] = useState("");
  const [notify, setNotify] = useState(true);
  const [saving, setSaving] = useState(false);
  const toUser = users.find((u) => u.id === toUserId);

  useEffect(() => {
    api.get(`/users/${fromUser.id}/record-counts`).then((r) => setCounts(r.data)).catch(() => setCounts({ leads: 0, opportunities: 0, tasks: 0 }));
  }, [fromUser.id]);

  const total = counts ? (counts.leads + counts.opportunities + counts.tasks) : 0;

  const submit = async (e) => {
    e.preventDefault();
    if (!toUserId) return;
    setSaving(true);
    try {
      const { data } = await api.post(`/users/${fromUser.id}/reassign-all`, { to_user_id: toUserId, notify });
      const parts = [];
      if (data.leads) parts.push(`${data.leads} lead${data.leads === 1 ? "" : "s"}`);
      if (data.opportunities) parts.push(`${data.opportunities} opportunit${data.opportunities === 1 ? "y" : "ies"}`);
      if (data.tasks) parts.push(`${data.tasks} task${data.tasks === 1 ? "" : "s"}`);
      toast.success(parts.length ? `Reassigned ${parts.join(" · ")} to ${toUser?.name}` : "Nothing to reassign");
      onDone();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="reassign-all-modal">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <div>
            <h3 className="font-heading text-lg font-semibold">Reassign all records</h3>
            <div className="text-xs text-slate-500 mt-0.5">From <span className="font-semibold">{fromUser.name}</span></div>
          </div>
          <button onClick={onClose}><X className="w-4 h-4 text-slate-400" /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <div className="grid grid-cols-3 gap-2">
            <Stat label="Leads" value={counts?.leads ?? "…"} />
            <Stat label="Opportunities" value={counts?.opportunities ?? "…"} />
            <Stat label="Tasks" value={counts?.tasks ?? "…"} />
          </div>

          {total === 0 && counts && (
            <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2">
              This user has no records to reassign.
            </div>
          )}

          <label className="block">
            <span className="eyb-label">New owner</span>
            <select
              data-testid="reassign-to-select"
              required
              value={toUserId}
              onChange={(e) => setToUserId(e.target.value)}
              className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#29ABE2]/40"
            >
              <option value="">— select a user —</option>
              {users.map((u) => <option key={u.id} value={u.id}>{u.name} · {u.role.replace("_", " ")}</option>)}
            </select>
          </label>

          <label className="flex items-start gap-3 p-3 border border-slate-200 rounded-lg cursor-pointer hover:bg-slate-50">
            <input
              data-testid="reassign-notify-checkbox"
              type="checkbox"
              checked={notify}
              onChange={(e) => setNotify(e.target.checked)}
              className="mt-0.5 w-4 h-4 accent-[#29ABE2]"
            />
            <div>
              <div className="text-sm font-semibold text-slate-800">Notify the new owner by email</div>
              <div className="text-xs text-slate-500 mt-0.5">
                {toUser ? <>One summary email to <span className="font-medium">{toUser.email}</span></> : "A single summary email when checked"}
              </div>
            </div>
          </label>

          <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-50 rounded-md">Cancel</button>
            <button data-testid="submit-reassign-all" type="submit" disabled={saving || !toUserId || total === 0} className="px-4 py-2 bg-[#29ABE2] hover:bg-[#1f8ebf] disabled:opacity-50 text-white text-sm font-semibold rounded-full">
              {saving ? "Reassigning…" : "Reassign all"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-center">
      <div className="text-[10px] tracking-wider uppercase text-slate-500">{label}</div>
      <div className="font-heading text-2xl font-bold text-slate-900 mt-1">{value}</div>
    </div>
  );
}

